import { Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FlexCenVer } from '@/UI/styles';
import { AsteriskIcon } from '@/UI/Icons/AsteriskIcon';
import { PermissionsContainer } from '@/components/PermissionsContainer/PermissionsContainer';
import { color } from '@/colors';
import { Tag } from '@/UI/Tag/Tag';
import { I18N_KEYS } from '@/language/keys';
import { useCreditsBalanceDisplay } from '@/hooks/useCreditsBalanceDisplay';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';

export interface WorkflowCreditsDisplayProps {
  showCreditsLabel?: boolean;
}

export function WorkflowCreditsDisplay({ showCreditsLabel = false }: WorkflowCreditsDisplayProps) {
  const { creditsDisplayText, isLowCredits, handleGetMoreCreditsClick } = useCreditsBalanceDisplay();
  const { isAllowedForPermission } = useSubscriptionPermissions();
  const { t } = useTranslation();
  const isCreditsActionAllowed = isAllowedForPermission('credits', 'action');

  return (
    <PermissionsContainer permission="credits">
      <Tooltip
        enterDelay={800}
        enterNextDelay={800}
        title={t(I18N_KEYS.FLOW_NAVBAR.GET_MORE_CREDITS_V2)}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -10],
                },
              },
            ],
          },
        }}
      >
        <FlexCenVer
          sx={{
            height: 24,
            gap: 0.25,
            cursor: 'pointer',
            pointerEvents: isCreditsActionAllowed ? 'auto' : 'none',
            px: 0.5,
            borderRadius: 1,
            '&:hover': { bgcolor: color.Black84 },
          }}
          onClick={handleGetMoreCreditsClick}
        >
          <AsteriskIcon width={16} height={16} />
          {isLowCredits ? (
            <FlexCenVer sx={{ gap: 0.75 }}>
              <Typography variant="body-sm-rg">{creditsDisplayText}</Typography>
              <Tag
                disableTextTransform
                text={t(I18N_KEYS.FLOW_NAVBAR.LOW_CREDITS_LABEL)}
                bgColor={color.Yellow16_T}
                textColor={color.Yellow100}
              />
            </FlexCenVer>
          ) : (
            <Typography variant="body-sm-rg">
              {showCreditsLabel
                ? t(I18N_KEYS.GENERAL.CREDITS_DISPLAY, { credits: creditsDisplayText })
                : creditsDisplayText}
            </Typography>
          )}
        </FlexCenVer>
      </Tooltip>
    </PermissionsContainer>
  );
}
